import React, { useState } from 'react';
import { Users, Search, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, SectionHeader } from '../ui';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useHOA } from '../../contexts/HOAContext';
import { hoaProfile } from '../../lib/hoaProfile';
import { useModalDismiss } from '../ui/useModalDismiss';

const ROLE_DISPLAY = {
  admin:      'HOA Administrator',
  board:      'Board Member',
  management: 'Property Management',
  member:     'Homeowner',
};

const ROLE_BADGE = {
  admin:      'bg-secondary/10 text-forest-900 border border-forest-300',
  board:      'bg-blue-50 text-blue-800 border border-blue-200',
  management: 'bg-amber-50 text-amber-800 border border-amber-200',
  member:     'bg-stone-100 text-stone-700 border border-stone-300',
};

/**
 * getVisibleColumns — returns the set of columns visible at a given permission level.
 * permission_level mapping (from roles table):
 *   member     = 10
 *   management = 40
 *   board      = 60
 *   admin      = 100
 */
const getVisibleColumns = (permissionLevel) => {
  const cols = new Set(['name', 'role', 'avatar']);
  if (permissionLevel >= 40) { cols.add('email'); cols.add('phone'); }
  if (permissionLevel >= 60) { cols.add('status'); cols.add('since'); }
  if (permissionLevel >= 100) { cols.add('verified'); }
  return cols;
};

const MemberDirectory = ({ viewerPermissionLevel = 10, sectionTitle = 'Member Directory' }) => {
  const { user, startImpersonating } = useAuth();
  const { hoa } = useHOA();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  useModalDismiss({ isOpen: Boolean(selectedMember), onClose: () => setSelectedMember(null) });

  const cols = getVisibleColumns(viewerPermissionLevel);

  React.useEffect(() => {
    const fetchMembers = async () => {
      if (!hoa?.id) return;
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select(`
            id,
            full_name,
            email,
            phone,
            avatar_url,
            is_active,
            is_verified,
            created_at,
            user_roles!user_roles_user_id_fkey!inner (
              roles (
                name,
                display_name,
                permission_level
              )
            )
          `)
          .eq('hoa_id', hoa.id)
          .eq('is_active', true)
          .order('full_name');

        if (error) throw error;

        if (data) {
          const mapped = data.map(u => {
            const roleEntry = Array.isArray(u.user_roles)
              ? u.user_roles[0]
              : u.user_roles;
            const roleName = (roleEntry?.roles?.name || 'member').toLowerCase();
            return {
              id: u.id,
              name: u.full_name || '—',
              email: u.email || '—',
              phone: u.phone || '—',
              avatarInitial: (u.full_name || '?').charAt(0).toUpperCase(),
              isActive: u.is_active,
              isVerified: u.is_verified,
              since: u.created_at ? new Date(u.created_at).getFullYear().toString() : '—',
              roleKey: roleName,
              roleLabel: ROLE_DISPLAY[roleName] || roleName,
              roleBadge: ROLE_BADGE[roleName] || 'bg-slate-100 text-slate-600 border border-slate-200',
            };
          });
          setMembers(mapped);
        }
      } catch (err) {
        console.error('MemberDirectory: fetch error', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [hoa?.id]);

  const filtered = members.filter(m => {
    const q = searchTerm.toLowerCase();
    return (
      m.name.toLowerCase().includes(q) ||
      m.roleLabel.toLowerCase().includes(q) ||
      m.roleKey.toLowerCase().includes(q) ||
      (cols.has('email') && m.email.toLowerCase().includes(q)) ||
      (cols.has('phone') && m.phone.toLowerCase().includes(q))
    );
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <SectionHeader icon={Users} title={sectionTitle} />
        <Card>
          <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
            Loading members…
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeader icon={Users} title={sectionTitle} />

      <Card>
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search members…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 shadow-sm hover:border-forest-300 focus:border-secondary focus:ring-2 focus:ring-secondary/10 rounded-xl text-slate-800 placeholder-slate-400 text-base md:text-sm outline-none transition-all"
          />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 bg-white rounded-2xl warm-tactile-shadow text-center px-8">
          <div className="w-16 h-16 rounded-2xl bg-section-bg flex items-center justify-center mb-6">
            <span className="material-symbols-outlined text-4xl text-secondary">person_search</span>
          </div>
          <h4 className="text-lg font-bold text-primary-dark mb-2">No members found</h4>
          <p className="text-sm text-slate-500 font-medium max-w-xs">Try a different name or address. The directory includes all registered {hoaProfile(hoa).shortName} homeowners.</p>
        </div>
      ) : (
        <>
        {/* Desktop: data table */}
        <div className="hidden md:block bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">Member</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">Role</th>
                {cols.has('email') && (
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">Contact</th>
                )}
                {cols.has('since') && (
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">Since</th>
                )}
                {cols.has('email') && (
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(m => (
                <tr key={m.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-secondary/10 border border-secondary/30 flex items-center justify-center text-secondary font-bold text-sm flex-shrink-0">
                        {m.avatarInitial}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{m.name}</div>
                        {cols.has('status') && (
                          <div className={`text-xs mt-0.5 ${m.isActive ? 'text-emerald-600' : 'text-red-500'}`}>
                            {m.isActive ? 'Active' : 'Inactive'}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-0.5 text-xs rounded-full font-medium ${m.roleBadge}`}>
                      {m.roleLabel}
                    </span>
                  </td>
                  {cols.has('email') && (
                    <td className="px-6 py-4">
                      <div className="text-xs text-slate-600 space-y-0.5">
                        <div>{m.email}</div>
                        <div className="text-slate-400">{m.phone}</div>
                      </div>
                    </td>
                  )}
                  {cols.has('since') && (
                    <td className="px-6 py-4 text-sm text-slate-600">{m.since}</td>
                  )}
                  {cols.has('email') && (
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setSelectedMember(m)}
                          className="text-xs text-secondary hover:text-secondary font-medium"
                        >
                          View
                        </button>
                        {viewerPermissionLevel >= 100 && m.id !== user?.id && (
                          <button
                            onClick={() => startImpersonating({
                              id: m.id,
                              full_name: m.name,
                              email: m.email,
                              role: m.roleKey,
                              permission_level: m.permissionLevel || 0,
                            })}
                            className="px-3 py-1 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
                          >
                            View as
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: stacked card view */}
        <div className="block md:hidden space-y-3">
          {filtered.map(m => (
            <div
              key={m.id}
              className="bg-white rounded-2xl p-5 warm-tactile-shadow border border-slate-200/40 min-h-[48px]"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-secondary/10 border border-secondary/30 flex items-center justify-center text-secondary font-bold text-sm flex-shrink-0">
                  {m.avatarInitial}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-primary-dark text-base truncate">{m.name}</p>
                  <span className={`inline-block mt-0.5 px-2.5 py-0.5 text-xs rounded-full font-medium ${m.roleBadge}`}>
                    {m.roleLabel}
                  </span>
                </div>
              </div>
              {cols.has('email') && (
                <div className="space-y-1 text-xs text-slate-600 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-sm text-slate-400">mail</span>
                    {m.email}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-sm text-slate-400">phone</span>
                    {m.phone}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                {cols.has('email') && (
                  <button
                    onClick={() => setSelectedMember(m)}
                    className="flex-1 text-center text-xs text-secondary hover:text-secondary font-semibold py-2 rounded-xl hover:bg-secondary/5 transition-colors min-h-[44px] flex items-center justify-center"
                  >
                    View Details
                  </button>
                )}
                {viewerPermissionLevel >= 100 && m.id !== user?.id && (
                  <button
                    onClick={() => startImpersonating({
                      id: m.id,
                      full_name: m.name,
                      email: m.email,
                      role: m.roleKey,
                      permission_level: m.permissionLevel || 0,
                    })}
                    className="flex-1 text-center px-3 py-2 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-xl hover:bg-indigo-50 transition-colors min-h-[44px] flex items-center justify-center"
                  >
                    View as
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {/* Detail modal — only shown for viewers with contact access */}
      <AnimatePresence>
        {selectedMember && cols.has('email') && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" role="presentation" data-modal-backdrop="true"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSelectedMember(null)}
          >
            <motion.div
              className="bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 max-w-md w-full p-6" role="dialog" aria-modal="true" aria-label="Member details"
              initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-display font-bold text-slate-900 text-lg">Member Details</h3>
                <button
                  onClick={() => setSelectedMember(null)}
                  aria-label="Close member details" className="w-10 h-10 text-slate-400 hover:text-slate-700 hover:bg-section-bg rounded-full flex items-center justify-center"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                {[
                  ['Name', selectedMember.name],
                  ['Role', selectedMember.roleLabel],
                  cols.has('email') && ['Email', selectedMember.email],
                  cols.has('phone') && ['Phone', selectedMember.phone],
                  cols.has('since') && ['Member Since', selectedMember.since],
                  cols.has('status') && ['Status', selectedMember.isActive ? 'Active' : 'Inactive'],
                  cols.has('verified') && ['Verified', selectedMember.isVerified ? 'Yes' : 'No'],
                ].filter(Boolean).map(([lbl, val]) => (
                  <div key={lbl}>
                    <span className="block text-xs text-slate-500 font-semibold uppercase tracking-wide mb-0.5">{lbl}</span>
                    <span className="text-slate-800 text-sm">{val}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MemberDirectory;
